import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IoService } from '../io/io.service';
import { AuthenticationService } from '../authentication/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class ClientSettingsService {

  clientSettings: any = null;

  private clientSettingsLoaded = new BehaviorSubject<any>(null);
  private inflight: Promise<any> | null = null;

  constructor(
    private ioService: IoService,
    private authenticationService: AuthenticationService,
  ) {
    // Auto-load on authentication; clear on sign-out so a different user
    // can never see the previous user's cached settings.
    this.authenticationService.authenticationStateChangedObservable().subscribe(authenticatedId => {
      if (authenticatedId) {
        this.loadClientSettings().catch(() => { /* keep current value */ });
      } else {
        this.clearClientSettings();
      }
    });
    // The auth state may have been emitted before this service was instantiated
    // (the auth Subject is non-replaying), so kick off an initial load if already signed in.
    if (this.authenticationService.isSignedIn()) {
      this.loadClientSettings().catch(() => { /* keep current value */ });
    }
  }

  clientSettingsLoadedObservable(): Observable<any> {
    return this.clientSettingsLoaded.asObservable();
  }

  async loadClientSettings(force: boolean = false): Promise<any> {
    if (!force && this.clientSettings) return this.clientSettings;
    if (this.inflight) return this.inflight;

    this.inflight = (async () => {
      try {
        const result: any = await this.ioService.post('/client/getClientSettings');
        this.clientSettings = result || {};
      } catch (e) {
        this.clientSettings = {};
      } finally {
        this.inflight = null;
      }
      this.clientSettingsLoaded.next(this.clientSettings);
      return this.clientSettings;
    })();
    return this.inflight;
  }

  clearClientSettings() {
    this.clientSettings = null;
    this.inflight = null;
    this.clientSettingsLoaded.next(null);
  }

  async getClientSetting(settingName: string, useCache: boolean = true) {
    let setting: any = { success: false };

    if (!useCache || !this.clientSettings) {
      await this.loadClientSettings(!useCache);
    }

    if (this.clientSettings && settingName in this.clientSettings) {
      setting.success = true;
      setting.value = this.clientSettings[settingName];
    }
    return setting;
  }

  // Weight/price-embedded barcode formats (settings.barcodeParsers), shared
  // with the ISE register; empty until settings load or when none configured.
  getBarcodeParsers(): any[] {
    const parsers = this.clientSettings?.barcodeParsers;
    return Array.isArray(parsers) ? parsers : [];
  }

  // Synchronous flag accessors (return false until settings are loaded).
  isReportVariantsEnabled(): boolean {
    return this.clientSettings?.reportVariants === true;
  }

  // Transaction corrections: adjust a mistaken transaction to a target value
  // via an additional, clearly-marked transaction. Opt-in per client.
  isCorrectionsEnabled(): boolean {
    return this.clientSettings?.correctionsEnabled === true;
  }

  // When enabled, a note explaining the correction is mandatory.
  isCorrectionsRequireNote(): boolean {
    return this.clientSettings?.correctionsRequireNote === true;
  }

  // Client-level dynamic View routing: settings.viewKeys maps a data type to
  // the View that renders its detail (e.g. { inventoryTransaction:
  // 'inventory-transaction' }). Absent = legacy detail component, so clients
  // without the new Views are unaffected.
  getViewKey(dataType: string): string | null {
    return this.clientSettings?.viewKeys?.[dataType] || null;
  }

  // Device authorization for PIN sign-in (settings.deviceAuthorization).
  // The server clamps and enforces these; the client only needs them to
  // decide whether to offer the "authorize this device" control and to
  // default/limit the days field. Disabled unless explicitly enabled.
  getDeviceAuthorizationSettings(): { enabled: boolean, maxDays: number, pinSessionHours: number, idleLockMinutes: number } {
    const raw = this.clientSettings?.deviceAuthorization || {};
    const num = (value, fallback) => (typeof value === 'number' && value > 0) ? value : fallback;
    return {
      enabled: raw.enabled === true,
      maxDays: num(raw.maxDays, 30),
      pinSessionHours: num(raw.pinSessionHours, 18),
      idleLockMinutes: num(raw.idleLockMinutes, 60),
    };
  }

  // Client-wide default for the on-screen numberpad on phone-width devices.
  // Defaults to true; clients whose handheld fleet has physical keypads
  // (e.g. Honeywell EDA61K) set settings.showNumberpad = false.
  isShowNumberpadDefault(): boolean {
    return this.clientSettings?.showNumberpad !== false;
  }

  // Client-wide toggle for the transfer Name field (create form, transfer
  // header, settings modal). Defaults to true; clients that identify
  // transfers by number alone set settings.transferNames = false.
  isTransferNamesEnabled(): boolean {
    return this.clientSettings?.transferNames !== false;
  }

  // Mobile transfers: when true, scanning or tapping a DIFFERENT item while
  // the numberpad has unsaved changes saves the edited line first (identical
  // to pressing Save Change) instead of silently abandoning it. Off (absent)
  // = legacy behavior for existing clients.
  isAutoSaveOnScanEnabled(): boolean {
    return this.clientSettings?.autoSaveOnScan === true;
  }

  // When true, the transfers UI shows the Item # (first barcode) as the
  // item identifier wherever the SKU shows by default — item cells, print
  // layout, and search results. Off (absent) = legacy SKU display.
  isItemNumberPrimary(): boolean {
    return this.clientSettings?.itemNumberPrimary === true;
  }

  // Client-defined Retail Product fields surfaced in the Sale Campaign
  // Manager's Find Items step as filters and shown on result/selected rows.
  // Array of { key, name } referencing Account fields (e.g. attr).
  getSaleCampaignItemFields(): Array<any> {
    return Array.isArray(this.clientSettings?.saleCampaignItemFields)
      ? this.clientSettings.saleCampaignItemFields
      : [];
  }

  // Defaults for the Bulk Add wizard's sales-based replenishment; each run can
  // override them in the wizard.
  getReplenishWindowDays(): number {
    const value = this.clientSettings?.replenishWindowDays;
    return (typeof value === 'number' && value > 0) ? value : 42;
  }

  getReplenishTargetDays(): number {
    const value = this.clientSettings?.replenishTargetDays;
    return (typeof value === 'number' && value > 0) ? value : 14;
  }

  // The Stock Targets (par level) replenish strategy is opt-in per client;
  // sales-based days of supply is the default strategy everywhere.
  isReplenishStockTargetsEnabled(): boolean {
    return this.clientSettings?.replenishStockTargets === true;
  }

  // When true, warehouse locations the user is authorized for are offered in
  // the header location picker alongside retail locations, so reports and
  // detail panels can be scoped to a warehouse. Off (absent) = retail only.
  isWarehouseLocationPickerEnabled(): boolean {
    return this.clientSettings?.warehouseLocationPicker === true;
  }
}